import { useEffect, useId, useRef, useState, type FormEvent, type ReactNode } from "react";
import { useNavigate } from "react-router";
import { useQueryClient } from "@tanstack/react-query";
import { ApiError, errorMessage, isPending, type ActionResult } from "../api";
import { Callout } from "./bits";
import { useToast } from "./toast";

/** A refused action may carry the findings that stopped it (details.issues, like a failed course check): the first few, for the message. */
function issueLines(e: unknown): string {
  if (!(e instanceof ApiError)) return "";
  const issues = (e.details as { issues?: { path?: string; message?: string }[] } | undefined)?.issues;
  if (!Array.isArray(issues) || !issues.length) return "";
  const shown = issues.slice(0, 5).map((i) => (i.path ? i.path + ": " : "") + i.message);
  return " " + shown.join(" · ") + (issues.length > shown.length ? ` · and ${issues.length - shown.length} more` : "");
}

/** Native modal dialog: Esc closes, focus is trapped by the browser. */
export function Dialog({
  open,
  onClose,
  title,
  children,
  footer,
  wide,
  danger,
  onSubmit,
  testid,
}: {
  open: boolean;
  onClose: () => void;
  title: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
  wide?: boolean;
  danger?: boolean;
  onSubmit?: (e: FormEvent) => void;
  testid?: string;
}) {
  const ref = useRef<HTMLDialogElement>(null);
  const titleId = useId();
  useEffect(() => {
    const d = ref.current;
    if (!d) return;
    if (open && !d.open) {
      d.showModal();
      // focus the first field, not the close button
      const first = d.querySelector<HTMLElement>("[data-autofocus], .dlg-body input:not([type=checkbox]):not([disabled]), .dlg-body select, .dlg-body textarea");
      first?.focus();
    } else if (!open && d.open) d.close();
  }, [open]);
  return (
    <dialog
      ref={ref}
      className={"dlg" + (wide ? " wide" : "") + (danger ? " danger" : "")}
      aria-labelledby={titleId}
      onCancel={(e) => {
        e.preventDefault();
        onClose();
      }}
      data-testid={testid}
    >
      {open && (
        <form
          method="dialog"
          onSubmit={(e) => {
            e.preventDefault();
            onSubmit?.(e);
          }}
        >
          <div className="dlg-head">
            <h2 id={titleId}>{title}</h2>
            <button type="button" className="ghost small" aria-label="Close dialog" onClick={onClose}>
              ✕
            </button>
          </div>
          <div className="dlg-body">{children}</div>
          {footer && <div className="dlg-foot">{footer}</div>}
        </form>
      )}
    </dialog>
  );
}

/**
 * Every console write goes through this dialog: it asks for the reason the
 * audit log needs (≥ 5 characters, admin-api checks it too), shows the API's
 * error inline, and tells the user when four-eyes turned the write into an
 * approval request (HTTP 202).
 */
export function ActionDialog({
  open,
  onClose,
  title,
  description,
  children,
  confirmLabel = "Confirm",
  danger,
  requireReason = true,
  reasonLabel = "Reason (goes in the audit log)",
  reasonPlaceholder,
  valid = true,
  onSubmit,
  onDone,
  successText,
  invalidate,
  wide,
  testid,
  pendingNote,
}: {
  open: boolean;
  onClose: () => void;
  title: ReactNode;
  description?: ReactNode;
  children?: ReactNode;
  confirmLabel?: string;
  danger?: boolean;
  requireReason?: boolean;
  reasonLabel?: string;
  reasonPlaceholder?: string;
  valid?: boolean;
  onSubmit: (reason: string) => Promise<unknown>;
  onDone?: (res: unknown) => void;
  successText?: string | ((res: unknown) => string);
  /** react-query keys to refresh after success (done or queued). */
  invalidate?: unknown[][];
  wide?: boolean;
  testid?: string;
  /** Said under "Sent for approval" when four-eyes queued the request (what is waiting, and for whom). */
  pendingNote?: ReactNode;
}) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState<{ id: string; message?: string } | null>(null);
  const toast = useToast();
  const qc = useQueryClient();
  const navigate = useNavigate();

  useEffect(() => {
    if (open) {
      setReason("");
      setError(null);
      setPending(null);
      setBusy(false);
    }
  }, [open]);

  const reasonOk = !requireReason || reason.trim().length >= 5;
  const submit = async () => {
    if (!reasonOk || !valid || busy) return;
    setBusy(true);
    setError(null);
    try {
      const res = await onSubmit(reason.trim());
      invalidate?.forEach((k) => qc.invalidateQueries({ queryKey: k }));
      qc.invalidateQueries({ queryKey: ["approvals"] });
      if (isPending(res as ActionResult)) {
        const r = res as { approval_id: string; message?: string };
        setPending({ id: r.approval_id, message: r.message });
        toast("pending", "Sent for approval. Another staff member must approve it.");
      } else {
        const text = typeof successText === "function" ? successText(res) : successText || "Done.";
        toast("ok", text);
        onDone?.(res);
        onClose();
      }
    } catch (e) {
      setError(errorMessage(e) + issueLines(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={title}
      danger={danger}
      wide={wide}
      onSubmit={submit}
      testid={testid}
      footer={
        pending ? (
          <>
            <button type="button" onClick={() => navigate("/approvals")}>
              Open approvals
            </button>
            <button type="button" className="primary" onClick={onClose} data-autofocus>
              Close
            </button>
          </>
        ) : (
          <>
            <button type="button" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className={danger ? "danger" : "primary"} disabled={!reasonOk || !valid || busy}>
              {busy && <span className="spinner" />}
              {confirmLabel}
            </button>
          </>
        )
      }
    >
      {pending ? (
        <div data-testid="sent-for-approval">
          <Callout kind="info">
            <strong>Sent for approval.</strong> {pending.message || "A second person must approve it. It's in the approvals queue."}
            {pendingNote && <div style={{ marginTop: 4 }}>{pendingNote}</div>}
            <div className="small mono" style={{ marginTop: 4 }}>
              Approval {pending.id}
            </div>
          </Callout>
        </div>
      ) : (
        <>
          {description && <div className="muted">{description}</div>}
          {children}
          {requireReason && (
            <label className="field">
              <span className="label">{reasonLabel}</span>
              <textarea
                name="reason"
                rows={2}
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder={reasonPlaceholder || "Why this is needed (at least 5 characters)"}
                aria-invalid={reason.length > 0 && !reasonOk}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
                    e.preventDefault();
                    void submit();
                  }
                }}
              />
              <span className="hint">
                {reason.trim().length}/5 minimum · <kbd>Ctrl</kbd>+<kbd>Enter</kbd> to submit
              </span>
            </label>
          )}
          {error && (
            <div role="alert" data-testid="action-error">
              <Callout kind="danger">{error}</Callout>
            </div>
          )}
        </>
      )}
    </Dialog>
  );
}

/** A simple yes/no confirmation for things without a reason field. */
export function Confirm({
  open,
  onClose,
  title,
  children,
  confirmLabel = "Confirm",
  danger,
  onConfirm,
}: {
  open: boolean;
  onClose: () => void;
  title: ReactNode;
  children: ReactNode;
  confirmLabel?: string;
  danger?: boolean;
  onConfirm: () => Promise<unknown> | void;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    if (open) setError(null);
  }, [open]);
  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={title}
      danger={danger}
      onSubmit={async () => {
        setBusy(true);
        try {
          await onConfirm();
          onClose();
        } catch (e) {
          setError(errorMessage(e));
        } finally {
          setBusy(false);
        }
      }}
      footer={
        <>
          <button type="button" onClick={onClose}>
            Cancel
          </button>
          <button type="submit" className={danger ? "danger" : "primary"} disabled={busy} data-autofocus>
            {busy && <span className="spinner" />}
            {confirmLabel}
          </button>
        </>
      }
    >
      {children}
      {error && (
        <div role="alert" data-testid="action-error">
          <Callout kind="danger">{error}</Callout>
        </div>
      )}
    </Dialog>
  );
}
